/**
 * useRentalCalcPricing — P0-1 (7-kör FE-2) V2-ár render-only hook.
 *
 * A V2-sidebar BE-authoritative árazásának forrása: a `RentalCalcRequest`-re (cart+dates+
 * accessory — a hívó V2-komponens állítja össze) debounced `POST /rentals/calculate` (#501)
 * hívás, a `RentalCalcResponse`-t adja vissza **render-only**-an. A FE-only
 * `wizard-store.calculateCartPricing` helyett — a BE az SSoT (pénz-helyesség P0); a FE-side
 * volume-discount NEM fut a V2-ágon (a `discountTotal` a BE-ből: most tier-only = 0, az
 * ADR-048 duration-discount P1-slice-szal tér vissza).
 *
 * Minta: `useReturnAccessories`/`useConsignmentsForRental` (useState + useEffect, NEM React Query)
 * + 300ms debounce a serialized input-ra (a calc-input gyors mutációja ne spamolja a BE-t).
 * A debounced key a TRIGGER (change-detektálás); a tényleges body az élő input ref-ből
 * (a `JSON.parse` a Date-eket elrontaná — a transport-réteg serializálja).
 */
import { calculateRentalPricing } from '@/api/rental-pricing';
import type { RentalCalcRequest, RentalCalcResponse } from '@kgc/rental-core/rental-dto';
import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from './use-debounced-value';

interface UseRentalCalcPricingResult {
  pricing: RentalCalcResponse | null;
  isLoading: boolean;
  error: string | null;
}

export function useRentalCalcPricing(input: RentalCalcRequest | null): UseRentalCalcPricingResult {
  const [pricing, setPricing] = useState<RentalCalcResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Change-detektálás: a serialized input. A debounced key a trigger (300ms);
  // a tényleges body az élő input ref-ből (Date-megőrzés — a JSON.parse string-gé tenné).
  const serialized = input ? JSON.stringify(input) : null;
  const debouncedKey = useDebouncedValue(serialized, 300);
  const inputRef = useRef<RentalCalcRequest | null>(input);
  inputRef.current = input;

  useEffect(() => {
    if (debouncedKey == null) {
      setPricing(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    const body = inputRef.current;
    if (!body) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    calculateRentalPricing(body)
      .then(res => {
        if (!cancelled) setPricing(res.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Az árazás lekérése sikertelen');
        setPricing(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedKey]);

  return { pricing, isLoading, error };
}
